/* eslint-disable import/prefer-default-export */
import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { AccountMappingPaymentLocation, AccountMappingPaymentLocationListResponse } from "api/models";
import { getAccountMappingPaymentLocations } from "api/services/user";
import { userQueryKeys } from "./userQueryKeys";

export const ACCOUNT_MAPPING_PAYMENT_LOCATION_PAGE_SIZE = 20;

export type AccountMappingPaymentLocationFilters = {
  query?: string;
  page_size?: number;
};

function normalizePaymentLocationPage(
  payload: AccountMappingPaymentLocation[] | AccountMappingPaymentLocationListResponse,
): AccountMappingPaymentLocationListResponse {
  if (Array.isArray(payload)) {
    return { count: payload.length, results: payload };
  }

  return {
    count: payload.count ?? payload.results?.length ?? 0,
    next: payload.next,
    previous: payload.previous,
    results: payload.results || [],
  };
}

export function useGetAccountMappingPaymentLocations(
  filters: AccountMappingPaymentLocationFilters = {},
  options: { enabled?: boolean } = {},
) {
  const pageSize = filters.page_size ?? ACCOUNT_MAPPING_PAYMENT_LOCATION_PAGE_SIZE;
  const query = filters.query?.trim() || "";

  const infiniteQuery = useInfiniteQuery({
    queryKey: userQueryKeys.accountMappingPaymentLocations({ query, page_size: pageSize }),
    enabled: options.enabled ?? true,
    queryFn: ({ pageParam = 1 }) =>
      getAccountMappingPaymentLocations({
        params: {
          page: pageParam,
          page_size: pageSize,
          ...(query ? { query } : {}),
        },
      }).then((res) =>
        normalizePaymentLocationPage(
          res.data as AccountMappingPaymentLocation[] | AccountMappingPaymentLocationListResponse,
        ),
      ),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length + 1;
      }

      const loadedCount = allPages.reduce((sum, page) => sum + page.results.length, 0);
      if (loadedCount < lastPage.count) {
        return allPages.length + 1;
      }

      return undefined;
    },
    keepPreviousData: true,
  });

  const paymentLocations = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.results) ?? [],
    [infiniteQuery.data],
  );

  return {
    ...infiniteQuery,
    paymentLocations,
  };
}
